/**
 * Rebalance Preview page - "What trades to reach target allocation?"
 */
import { useState, useMemo } from 'react'
import { useRebalancePreview } from '../hooks'
import { Button } from '../components/common'
import type { AllocationTarget, RebalanceResult } from '../types'

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value)
}

export function RebalancePreviewPage() {
  const { calculate, holdings } = useRebalancePreview()

  // Form state
  const [groupBy, setGroupBy] = useState<'portfolio' | 'tag'>('portfolio')
  const [targets, setTargets] = useState<Map<string, number>>(new Map())

  // Calculate current allocations
  const currentAllocations = useMemo(() => {
    const totalValue = holdings.reduce((sum, h) =>
      sum + (h.current_value ?? h.cost_basis), 0
    )

    if (groupBy === 'portfolio') {
      const byPortfolio = new Map<string, { name: string; value: number }>()

      for (const h of holdings) {
        const key = h.portfolio_id
        const name = h.portfolio_name || 'Unknown'
        const value = h.current_value ?? h.cost_basis

        if (!byPortfolio.has(key)) {
          byPortfolio.set(key, { name, value: 0 })
        }
        byPortfolio.get(key)!.value += value
      }

      return Array.from(byPortfolio.entries()).map(([id, data]) => ({
        id,
        name: data.name,
        currentValue: data.value,
        currentAllocation: totalValue > 0 ? (data.value / totalValue) * 100 : 0,
      }))
    } else {
      const byTag = new Map<string, { name: string; value: number }>()

      for (const h of holdings) {
        const value = h.current_value ?? h.cost_basis

        if (h.tags.length === 0) {
          if (!byTag.has('untagged')) {
            byTag.set('untagged', { name: 'Untagged', value: 0 })
          }
          byTag.get('untagged')!.value += value
        } else {
          for (const tagName of h.tags) {
            if (!byTag.has(tagName)) {
              byTag.set(tagName, { name: tagName, value: 0 })
            }
            byTag.get(tagName)!.value += value
          }
        }
      }

      return Array.from(byTag.entries()).map(([id, data]) => ({
        id,
        name: data.name,
        currentValue: data.value,
        currentAllocation: totalValue > 0 ? (data.value / totalValue) * 100 : 0,
      }))
    }
  }, [holdings, groupBy])

  const totalValue = holdings.reduce((sum, h) =>
    sum + (h.current_value ?? h.cost_basis), 0
  )

  // Handle target change
  const handleTargetChange = (id: string, value: number) => {
    const newTargets = new Map(targets)
    newTargets.set(id, value)
    setTargets(newTargets)
  }

  // Calculate total target allocation
  const totalTarget = useMemo(() => {
    let sum = 0
    for (const alloc of currentAllocations) {
      sum += targets.get(alloc.id) ?? alloc.currentAllocation
    }
    return sum
  }, [targets, currentAllocations])

  // Build allocation targets for calculation
  const allocationTargets: AllocationTarget[] = useMemo(() => {
    return currentAllocations.map(alloc => {
      const targetAllocation = targets.get(alloc.id) ?? alloc.currentAllocation
      return {
        id: alloc.id,
        name: alloc.name,
        currentAllocation: alloc.currentAllocation,
        targetAllocation,
        currentValue: alloc.currentValue,
        targetValue: totalValue * (targetAllocation / 100),
      }
    })
  }, [currentAllocations, targets, totalValue])

  // Calculate result
  const result: RebalanceResult | null = useMemo(() => {
    // Only calculate if targets differ from current
    const hasChanges = allocationTargets.some(t =>
      Math.abs(t.targetAllocation - t.currentAllocation) > 0.1
    )
    if (!hasChanges) return null

    return calculate(allocationTargets, groupBy)
  }, [allocationTargets, groupBy, calculate])

  // Reset to current allocations
  const handleReset = () => {
    setTargets(new Map())
  }

  // Equal weight all
  const handleEqualWeight = () => {
    const equalWeight = 100 / currentAllocations.length
    const newTargets = new Map<string, number>()
    for (const alloc of currentAllocations) {
      newTargets.set(alloc.id, equalWeight)
    }
    setTargets(newTargets)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Rebalance Preview</h1>
        <p className="mt-1 text-sm text-gray-500">
          Set target allocations and see the trades needed to rebalance
        </p>
      </div>

      {/* Group by selector */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-gray-700">Rebalance by:</span>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setGroupBy('portfolio')
                setTargets(new Map())
              }}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                groupBy === 'portfolio'
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Portfolio
            </button>
            <button
              onClick={() => {
                setGroupBy('tag')
                setTargets(new Map())
              }}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                groupBy === 'tag'
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Tag
            </button>
          </div>
          <div className="flex-1" />
          <Button variant="secondary" size="sm" onClick={handleReset}>
            Reset
          </Button>
          <Button variant="secondary" size="sm" onClick={handleEqualWeight}>
            Equal Weight
          </Button>
        </div>
      </div>

      {/* Allocation table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900">Target Allocations</h2>
          <div className={`text-sm font-medium ${
            Math.abs(totalTarget - 100) < 0.1
              ? 'text-green-600'
              : 'text-red-600'
          }`}>
            Total: {totalTarget.toFixed(1)}%
            {Math.abs(totalTarget - 100) >= 0.1 && (
              <span className="ml-2 text-xs">
                ({totalTarget > 100 ? '+' : ''}{(totalTarget - 100).toFixed(1)}%)
              </span>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {groupBy === 'portfolio' ? 'Portfolio' : 'Tag'}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Current Value
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Current %
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase w-48">
                  Target %
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Target Value
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Difference
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentAllocations.map((alloc) => {
                const targetPct = targets.get(alloc.id) ?? alloc.currentAllocation
                const targetValue = totalValue * (targetPct / 100)
                const diff = targetValue - alloc.currentValue

                return (
                  <tr key={alloc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {alloc.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(alloc.currentValue)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                      {alloc.currentAllocation.toFixed(1)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <input
                          type="range"
                          min={0}
                          max={100}
                          step={0.5}
                          value={targetPct}
                          onChange={(e) => handleTargetChange(alloc.id, parseFloat(e.target.value))}
                          className="flex-1"
                        />
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={0.1}
                          value={targetPct.toFixed(1)}
                          onChange={(e) => handleTargetChange(alloc.id, parseFloat(e.target.value) || 0)}
                          className="w-16 text-right text-sm border-gray-300 rounded"
                        />
                        <span className="text-gray-400">%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(targetValue)}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${
                      diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-600' : 'text-gray-400'
                    }`}>
                      {diff > 0 ? '+' : ''}{formatCurrency(diff)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Rebalance Plan</h2>
          </div>

          <div className="p-6 space-y-6">
            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-900">
                  {formatCurrency(result.totalBuyValue)}
                </div>
                <div className="text-sm text-green-700">Total Buys</div>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-red-900">
                  {formatCurrency(result.totalSellValue)}
                </div>
                <div className="text-sm text-red-700">Total Sells</div>
              </div>
              <div className={`rounded-lg p-4 text-center ${
                result.netCashFlow > 0 ? 'bg-yellow-50' : 'bg-blue-50'
              }`}>
                <div className={`text-2xl font-bold ${
                  result.netCashFlow > 0 ? 'text-yellow-900' : 'text-blue-900'
                }`}>
                  {result.netCashFlow > 0 ? '+' : ''}{formatCurrency(result.netCashFlow)}
                </div>
                <div className={`text-sm ${
                  result.netCashFlow > 0 ? 'text-yellow-700' : 'text-blue-700'
                }`}>
                  {result.netCashFlow > 0 ? 'Cash Needed' : 'Cash Generated'}
                </div>
              </div>
            </div>

            {/* Warnings */}
            {result.warnings.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-yellow-800 mb-2">Notes</h3>
                <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
                  {result.warnings.map((warning, i) => (
                    <li key={i}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Trades table */}
            {result.trades.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">Suggested Trades</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Action
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Symbol
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          Shares
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          Price
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          Value
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {result.trades.map((trade, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-2 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              trade.action === 'buy'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {trade.action.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                            {trade.symbol}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                            {trade.shares}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 text-right">
                            {formatCurrency(trade.pricePerShare)}
                          </td>
                          <td className={`px-4 py-2 whitespace-nowrap text-sm text-right font-medium ${
                            trade.action === 'buy' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {trade.action === 'buy' ? '+' : '-'}{formatCurrency(trade.tradeValue)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {result.trades.length === 0 && result.warnings.length > 0 && (
              <div className="text-center py-8 text-gray-500">
                <p>No specific trades can be suggested.</p>
                <p className="text-sm">Review the notes above for required actions.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!result && currentAllocations.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-12 text-center">
          <p className="text-gray-500">
            Adjust the target percentages above to see the rebalance plan
          </p>
        </div>
      )}

      {currentAllocations.length === 0 && (
        <div className="bg-gray-50 rounded-lg p-12 text-center">
          <p className="text-gray-500">
            No holdings found. Add holdings to use the rebalance preview.
          </p>
        </div>
      )}
    </div>
  )
}
